export interface ViewportRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface SurfaceRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export function viewportRect(): ViewportRect {
  const visual = window.visualViewport;
  return {
    left: visual?.offsetLeft ?? 0,
    top: visual?.offsetTop ?? 0,
    width: visual?.width ?? window.innerWidth,
    height: visual?.height ?? window.innerHeight,
  };
}

export function placeFloatingSurface(
  anchor: Point,
  size: { width: number; height: number },
  viewport: ViewportRect,
  options: {
    gap?: number;
    margin?: number;
    avoid?: SurfaceRect[];
  } = {},
): { left: number; top: number } {
  const gap = options.gap ?? 12;
  const margin = options.margin ?? 12;
  const avoid = options.avoid ?? [];
  const candidates = [
    { left: anchor.x + gap, top: anchor.y + gap },
    { left: anchor.x - size.width - gap, top: anchor.y + gap },
    { left: anchor.x + gap, top: anchor.y - size.height - gap },
    { left: anchor.x - size.width - gap, top: anchor.y - size.height - gap },
  ];

  return candidates
    .map((candidate, index) => {
      const placed = clampSurface(candidate, size, viewport, margin);
      const unclampedDistance =
        Math.abs(placed.left - candidate.left) + Math.abs(placed.top - candidate.top);
      const rect = { ...placed, ...size };
      const overlap = avoid.reduce(
        (total, blocked) => total + overlapArea(rect, blocked),
        0,
      );
      return { placed, score: overlap * 8 + unclampedDistance * 16 + index };
    })
    .sort((a, b) => a.score - b.score)[0].placed;
}

export function placePin(
  point: Point,
  diameter: number,
  viewport: ViewportRect,
  margin = 2,
): { left: number; top: number; visible: boolean } {
  const radius = diameter / 2;
  const right = viewport.left + viewport.width;
  const bottom = viewport.top + viewport.height;
  const visible =
    point.x >= viewport.left - radius &&
    point.x <= right + radius &&
    point.y >= viewport.top - radius &&
    point.y <= bottom + radius;
  const centerX = clamp(
    point.x,
    viewport.left + radius + margin,
    right - radius - margin,
  );
  const centerY = clamp(
    point.y,
    viewport.top + radius + margin,
    bottom - radius - margin,
  );
  return { left: centerX - radius, top: centerY - radius, visible };
}

export function rectFromDomRect(rect: DOMRect): SurfaceRect | null {
  if (rect.width <= 0 || rect.height <= 0) return null;
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  };
}

function clampSurface(
  point: { left: number; top: number },
  size: { width: number; height: number },
  viewport: ViewportRect,
  margin: number,
): { left: number; top: number } {
  const minLeft = viewport.left + margin;
  const minTop = viewport.top + margin;
  const maxLeft = Math.max(
    minLeft,
    viewport.left + viewport.width - size.width - margin,
  );
  const maxTop = Math.max(
    minTop,
    viewport.top + viewport.height - size.height - margin,
  );
  return {
    left: clamp(point.left, minLeft, maxLeft),
    top: clamp(point.top, minTop, maxTop),
  };
}

function overlapArea(a: SurfaceRect, b: SurfaceRect): number {
  const width = Math.max(
    0,
    Math.min(a.left + a.width, b.left + b.width) - Math.max(a.left, b.left),
  );
  const height = Math.max(
    0,
    Math.min(a.top + a.height, b.top + b.height) - Math.max(a.top, b.top),
  );
  return width * height;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}
